// frontend/src/components/Diario/ModalGlicemia.jsx
// Modal completo para registro de glicemia

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Droplet, Save, X, ChevronDown } from 'lucide-react';
import Modal from '../UI/Modal';
import DropdownMenu from '../UI/DropdownMenu.jsx';

const TURNOS = ['Manhã', 'Tarde', 'Noite', 'Madrugada'];

const MOMENTOS = [
    'Em jejum',
    'Pré-prandial (antes da refeição)',
    'Pós-prandial (após a refeição)',
    'Antes de dormir',
    'Outro'
];

/**
 * Modal completo para registro de glicemia
 */
const ModalGlicemia = ({ isOpen, onClose, pacienteId, onCreateRegistro, onRegistroSucesso }) => {
    const [formData, setFormData] = useState({
        glicemia: '',
        momento: '',
        observacoes: '',
        turno: 'Manhã'
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.glicemia.trim()) {
            newErrors.glicemia = 'Valor de glicemia é obrigatório';
        } else {
            const glicemiaNum = parseFloat(formData.glicemia.replace(',', '.'));
            if (isNaN(glicemiaNum) || glicemiaNum < 0) {
                newErrors.glicemia = 'Glicemia deve ser um número válido maior ou igual a zero';
            }
        }

        if (!formData.momento.trim()) {
            newErrors.momento = 'Momento da medição é obrigatório';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const glicemiaNum = parseFloat(formData.glicemia.replace(',', '.'));
            
            const dataToSend = {
                paciente_id: pacienteId,
                tipo_registro: 'Glicemia',
                observacoes: formData.observacoes || null,
                turno: formData.turno,
                detalhes: {
                    glicemia: glicemiaNum,
                    momento: formData.momento,
                    unidade: 'mg/dL'
                }
            };

            await onCreateRegistro(dataToSend);
            toast.success('Registro de glicemia salvo com sucesso!');
            
            if (onRegistroSucesso) {
                onRegistroSucesso();
            }
            
            // Resetar formulário
            setFormData({
                glicemia: '',
                momento: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        } catch (error) {
            toast.error(error.message || 'Erro ao registrar glicemia');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                glicemia: '',
                momento: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Registrar Glicemia" size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Glicemia e Momento - Grid */}
                <div className="grid grid-cols-2 gap-4">
                    {/* Glicemia */}
                    <div>
                        <label htmlFor="glicemia" className="block text-sm font-medium text-gray-700 mb-2">
                            <Droplet size={16} className="inline mr-2" />
                            Glicemia (mg/dL) <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="glicemia"
                            id="glicemia"
                            value={formData.glicemia}
                            onChange={handleChange}
                            placeholder="Ex: 120 ou 120,5"
                            className={`
                                mt-1 block w-full border rounded-md shadow-sm p-3 text-sm
                                focus:ring-emerald-500 focus:border-emerald-500
                                ${errors.glicemia ? 'border-red-300' : 'border-gray-300'}
                            `}
                        />
                        {errors.glicemia && (
                            <p className="mt-1 text-xs text-red-600">{errors.glicemia}</p>
                        )}
                    </div>

                    {/* Momento */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Momento da Medição <span className="text-red-500">*</span>
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    className={`
                                        mt-1 w-full flex items-center justify-between
                                        border rounded-md shadow-sm p-3 text-sm
                                        focus:ring-emerald-500 focus:border-emerald-500
                                        ${errors.momento ? 'border-red-300' : 'border-gray-300'}
                                        ${formData.momento ? 'text-gray-900' : 'text-gray-500'}
                                    `}
                                >
                                    <span>{formData.momento || 'Selecione o momento...'}</span>
                                    <ChevronDown size={18} className="text-gray-400" />
                                </button>
                            }
                            items={MOMENTOS.map(momento => ({
                                id: momento,
                                label: momento,
                                onClick: () => {
                                    setFormData(prev => ({ ...prev, momento }));
                                    if (errors.momento) {
                                        setErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.momento;
                                            return newErrors;
                                        });
                                    }
                                }
                            }))}
                            position="bottom-left"
                            className="w-full"
                        />
                        {errors.momento && (
                            <p className="mt-1 text-xs text-red-600">{errors.momento}</p>
                        )}
                    </div>
                </div>

                {/* Turno */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Turno <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                className="mt-1 w-full flex items-center justify-between border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                            >
                                <span>{formData.turno}</span>
                                <ChevronDown size={18} className="text-gray-400" />
                            </button>
                        }
                        items={TURNOS.map(turno => ({
                            id: turno,
                            label: turno,
                            onClick: () => {
                                setFormData(prev => ({ ...prev, turno }));
                            }
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* Observações */}
                <div>
                    <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-2">
                        Observações (Opcional)
                    </label>
                    <textarea
                        name="observacoes"
                        id="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        placeholder="Adicione observações sobre a glicemia..."
                        rows="4"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                    >
                        <X size={18} />
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md transition disabled:opacity-50"
                    >
                        {loading ? (
                            <>Salvando...</>
                        ) : (
                            <>
                                <Save size={18} />
                                Registrar Glicemia
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ModalGlicemia;

